import type { AssetsManifest } from 'pixi.js';

export type SpineBundles = {
    /** Manifest bundle names that contain spine assets. */
    names: string[];
    /**
     * Folder the spine aliases are addressed from, e.g. `spines` or `spine`. This is the
     * prefix `SpineLayout.createInstancesFromManifest` joins with each skeleton/atlas file name.
     */
    folder: string;
};

/**
 * Locates the spine assets in a built manifest.
 *
 * A game either commits a packed Spine export (`assets/spines/`) or the raw "export without
 * packing" that `@magic/assetpack`'s `spinePacker` packs during the build (`assets/spine/`),
 * so neither the bundle name nor the alias prefix can be assumed. Both are read off the `.atlas`
 * entries instead, which exist in either layout.
 */
export function findSpineBundles(manifest: AssetsManifest): SpineBundles {
    const names: string[] = [];
    let folder = '';

    manifest.bundles.forEach((bundle) => {
        if (!Array.isArray(bundle.assets)) return;

        const atlas = bundle.assets
            .flatMap((asset) => (asset as { alias?: string[] }).alias ?? [])
            .find((alias) => alias.endsWith('.atlas'));

        if (!atlas) return;

        names.push(bundle.name);

        // `alias[0]` is the full entry-relative path, so its directory is the alias prefix.
        if (!folder) folder = atlas.includes('/') ? atlas.slice(0, atlas.lastIndexOf('/')) : '';
    });

    return { names, folder };
}
